/**
 * useAspectCatalog.ts
 *
 * The org-wide, unscoped aspect (property) catalog shared by every surface
 * that lets an author bind a column/field to a sphere aspect — TreeMap's
 * advanced filter rail and the Atlas view editor's `SphereProperty` picker.
 *
 * Extracted from TreeMap's `orgWideFilterProperties` (EAINT-857 Phase 4):
 * that memo already answered "what aspects exist across every type in the
 * org", independent of any one column's scope. The Atlas editor needs
 * exactly that same unscoped catalog to populate `propertyOptions` — it has
 * no per-column eligibility concept the way TreeMap's columns do, so this
 * hook exposes only the unscoped half of TreeMap's property-building logic,
 * not `getEligibleSchemasForColumn`/`getScopeTypesForColumn`, which stay in
 * TreeMap because they depend on TreeMap-local column/level state.
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import type { ApiClients } from '../context/ApiClientsContext';
import type { PropertyOption } from '../components/molecules/PropertyFilter';
import { buildFilterPropertyOptions, type SchemaForFilters } from '../utils/filterPropertyBuilder';
import { useOrgAvailableTypes, type OrgAvailableTypesData } from './useOrgAvailableTypes';

/** Stable empty slice — a fresh `{}` per `useSelector` run would re-render every
 *  consumer (TreeMap, the Atlas editor) on every unrelated dispatch. */
const EMPTY_ARC_TEMPLATES = {};

export interface UseAspectCatalogReturn {
  /** The unscoped, org-wide aspect catalog, grouped by category. */
  propertyOptions: PropertyOption[];
  /** The raw org-wide schema catalog backing `propertyOptions` — callers that
   *  also need per-column eligibility (TreeMap) narrow this themselves. */
  orgFilterSchemas: OrgAvailableTypesData['schemas'];
  loading: boolean;
  error: string | null;
  /** Re-fetches the catalog, bypassing the server cache — the aspect picker's "Try again". */
  refetch: () => void;
}

/**
 * Builds the org-wide aspect catalog for the calling user (org + identity
 * resolved server-side from the token, via `useOrgAvailableTypes`).
 *
 * Owns the single `useOrgAvailableTypes` fetch for its consumers — callers
 * that also need the raw schema list (e.g. TreeMap's column-eligibility
 * narrowing) should read `orgFilterSchemas` off this hook's return rather
 * than calling `useOrgAvailableTypes` a second time, which would double the
 * `/schemas/org-available-types` request on every mount.
 *
 * @param apiClients - API clients (pass null if not ready; returns an empty catalog)
 */
export function useAspectCatalog(apiClients: ApiClients | null): UseAspectCatalogReturn {
  const { t } = useTranslation();
  const arcTemplates = useSelector((state: any) => state.arcTemplates || EMPTY_ARC_TEMPLATES);
  const {
    schemas: orgFilterSchemas,
    loading,
    error,
    refetch,
  } = useOrgAvailableTypes(apiClients);

  const propertyOptions = useMemo(() => {
    const orgSchemas = orgFilterSchemas as unknown as SchemaForFilters[];
    if (orgSchemas.length === 0) return [];
    return buildFilterPropertyOptions({
      schemas: orgSchemas,
      arcTemplates,
      t,
      includeArcProperties: true,
      includeCustomProperties: true,
      groupBy: 'category',
    });
  }, [orgFilterSchemas, arcTemplates, t]);

  return { propertyOptions, orgFilterSchemas, loading, error, refetch };
}
